import {
  BillingInterval,
  BillingProduct,
  BillingProductType,
  Currency,
} from '@citruscamps/citrus-client'
import { useFormik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import { JumboSelect } from '../../../../../components/JumboSelect/JumboSelect'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { Title } from '../../../../../components/Title/Title'
import translations from '../../../../../translations/en.json'
import { Address } from '../../../../../utils/address'
import { update } from '../../../../../utils/array-utils'
import { toCurrency } from '../../../../../utils/currency'
import { PlanDescription, PlanGraphic } from './PlanDescription'

interface IValue {
  interval: BillingInterval
  product_id?: string
  is_promotion?: boolean
}

interface BillingPlan {
  type: BillingProductType
  name: string
  products: BillingProduct[]
}

interface IProps {
  initialValues?: Omit<IValue, 'is_promotion'>
  products: BillingProduct[]
  language: string
  address?: Address
  onPrevious?: () => Promise<void> | void
  onSubmit: (value: BillingProduct) => Promise<void> | void
}

export const PlanStep = ({
  initialValues,
  products = [],
  language,
  address,
  onPrevious,
  onSubmit,
}: IProps) => {
  const initialProduct: BillingProduct | undefined = products.find(
    (p) => p.id === initialValues?.product_id,
  )
  const promotion: BillingProduct | undefined = products
    .sort((a, b) => b.amount.value - a.amount.value)
    .find((p) => p.interval === 'month' && p.active)
  const { values, errors, touched, handleSubmit, setFieldError, setFieldValue, isSubmitting } =
    useFormik<IValue>({
      initialValues: {
        interval: initialProduct?.interval || promotion?.interval || 'year',
        product_id: initialProduct?.id || promotion?.id,
        is_promotion: !initialProduct?.id,
      },
      validationSchema: Yup.object().shape({
        product_id: Yup.string().required((translations as any)['error.plan_required']),
      }),
      onSubmit: async (value: IValue, { setSubmitting }) => {
        try {
          const product = products.find((p) => p.id === value.product_id)
          if (!product) throw new Error((translations as any)['error.default_toast_message'])
          await onSubmit(product)
        } catch (e: any) {
          if (e.param) setFieldError(e.param, e.message)
        }
        setSubmitting(false)
      },
    })

  const plans: BillingPlan[] = products
    .sort((a, b) => a?.amount?.value - b?.amount.value)
    .reduce((items: BillingPlan[], _product: BillingProduct) => {
      const index = items.findIndex((i) => i.type === _product.type)
      if (index !== -1) {
        return update(
          items,
          { ...items[index], products: [...items[index].products, _product] },
          index,
        )
      }
      return [
        ...items,
        {
          type: _product.type,
          name: _product.name,
          products: [_product],
        },
      ]
    }, new Array<BillingPlan>())

  const product = products.find((p) => p.id === values.product_id)
  if (values.is_promotion && product) {
    return (
      <div>
        <Title value="Select a plan" />
        <h4 className="mb-4">Explore the {product.name} today</h4>
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <PlanGraphic product={product} />
          </div>
        </div>
        <div className="d-flex text-center mt-2">
          <PlanDescription product={product} language={language} address={address} />
        </div>
        <div className="text-center">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setFieldValue('is_promotion', false)}
          >
            View other plans
          </button>
        </div>
        <div className="d-flex justify-content-between mt-4">
          {onPrevious && (
            <button
              type="button"
              className="btn btn-link me-auto"
              disabled={isSubmitting}
              onClick={() => onPrevious()}
            >
              Back
            </button>
          )}
          <LoadingButton
            type="submit"
            className="btn btn-primary ms-auto"
            onClick={async () => {
              const product = products.find((p) => p.id === values.product_id)
              if (!product) throw new Error((translations as any)['error.default_toast_message'])
              await onSubmit(product)
            }}
          >
            Next
          </LoadingButton>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <Title value="Select a plan" />
      <h4 className="mb-4">Select a plan that fits your needs.</h4>
      <div className="mb-3">
        <div className="row justify-content-center g-0">
          <div className="col-md text-md-end py-3 text-center">
            <span className={values.interval === 'month' ? 'text-success' : ''}>Monthly</span>
          </div>
          <div className="col-md-4 d-flex py-3 text-center">
            <div className="form-check form-switch custom-switch-lg plan-switch mx-auto">
              <input
                type="checkbox"
                className="form-check-input d-inline"
                id="priceInterval"
                role="switch"
                checked={values.interval === 'year'}
                onChange={(evt) => {
                  const _interval: BillingInterval = evt.target.checked ? 'year' : 'month'
                  const _product: BillingProduct | undefined = products.find(
                    (p) =>
                      (p.type === product?.type && p.interval === _interval) ||
                      (product?.type === 'free' && p.type === 'free'),
                  )
                  setFieldValue('interval', _interval)
                  setFieldValue('product_id', _product?.id)
                }}
              />
              <label
                className="form-check-label"
                htmlFor="priceInterval"
                style={{ marginTop: '-0.5rem', marginRight: '-1rem' }}
              ></label>
            </div>
          </div>
          <div className="col-md text-md-start py-3 text-center">
            <span className={values.interval === 'year' ? 'text-primary' : ''}>Annually</span>
          </div>
        </div>
        <div className="invalid-feedback d-block">
          {touched.interval && !!errors.interval ? errors.interval : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <div className="row">
          <JumboSelect
            value={product}
            className="col-12 mb-3"
            invalid={touched.product_id && !!errors.product_id}
            types={plans.map((plan) => {
              const _value: BillingProduct =
                plan.products.find((p) => p.interval === values.interval) || plan.products[0]
              const price: Currency = toCurrency({
                amount:
                  _value.interval === 'month'
                    ? _value.amount.major_value
                    : Math.round(_value.amount.major_value / 12),
                currency: _value.amount.currency_code,
                language,
              })
              return {
                name: 'id',
                title: plan.name,
                description: (
                  <div className="d-flex">
                    <div className="h1">
                      <sup style={{ fontSize: '50%', top: '-0.75rem' }}>
                        {price.display.replace(/[0-9]|,|\./g, '')}
                      </sup>
                      <span>{price.major_value}</span>
                    </div>
                    <div className="ms-3 mb-2 mt-auto">
                      <div className="small">Per month</div>
                      <div>billed {_value.interval === 'month' ? 'monthly' : 'annually'}</div>
                      {price.major_value !== 0 && (
                        <div className="small">after 30-day free trial</div>
                      )}
                    </div>
                  </div>
                ),
                value: _value,
              }
            })}
            onChange={(value: BillingProduct) => {
              setFieldValue('product_id', value.id, true)
            }}
          />
        </div>
        <div className="invalid-feedback d-block">
          {touched.product_id && !!errors.product_id ? errors.product_id : '\u00A0'}
        </div>
      </div>
      <div className="d-flex justify-content-between mt-4">
        {onPrevious && (
          <button
            type="button"
            className="btn btn-link me-auto"
            disabled={isSubmitting}
            onClick={() => onPrevious()}
          >
            Back
          </button>
        )}
        <LoadingButton type="submit" className="btn btn-primary ms-auto" loading={isSubmitting}>
          Next
        </LoadingButton>
      </div>
    </form>
  )
}
